import { Listbox, Transition } from '@headlessui/react';
import clsx from 'clsx';
import type { Ref } from 'react';
import React, { forwardRef, Fragment } from 'react';
import { twMerge } from 'tailwind-merge';

import type {
  IListboxButtonProps,
  IListboxLabelProps,
  IListboxOptionProps,
  IListboxOptionsProps,
  IListBoxProps,
  IListboxWrapperProps,
} from './ISelectMenuProps';

const SelectMenuLabel: React.FC<IListboxLabelProps> = ({
  children,
  className,
  ...rest
}) => {
  return (
    <Listbox.Label
      className={twMerge('text-gray-600 text-sm', className)}
      {...rest}
    >
      {children}
    </Listbox.Label>
  );
};

const SelectMenuButton: React.FC<IListboxButtonProps> = forwardRef<
  HTMLButtonElement,
  IListboxButtonProps
>(
  (
    {
      children,
      prefixIcon: PrefixIcon = null,
      trailingIcon: TrailingIcon = null,
      className,
      ...rest
    },
    forwardedRef
  ) => {
    return (
      <Listbox.Button
        className={twMerge(
          clsx({
            'relative w-full cursor-default rounded-md border border-gray-400 bg-white px-3 pt-2 pb-2 text-left shadow-sm focus:border-2 focus:border-gray-600 focus:outline-none sm:text-sm':
              true,
            'pl-9': PrefixIcon !== null,
          }),
          className
        )}
        ref={forwardedRef as Ref<HTMLButtonElement>}
        {...rest}
      >
        <>
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            {PrefixIcon ? <PrefixIcon /> : null}
          </div>
          {children}
          {TrailingIcon ? (
            <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
              <TrailingIcon />
            </span>
          ) : null}
        </>
      </Listbox.Button>
    );
  }
);

SelectMenuButton.displayName = 'SelectMenuButton';

const SelectMenuOptions: React.FC<IListboxOptionsProps> = forwardRef<
  HTMLElement,
  IListboxOptionsProps
>(({ children, open = false, className, ...rest }, forwardedRef) => {
  return (
    <Transition
      show={open}
      as={Fragment}
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <Listbox.Options
        className={twMerge(
          'absolute z-10 mt-4 max-h-56 w-full overflow-auto rounded-lg bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
          className
        )}
        {...rest}
        ref={forwardedRef as Ref<HTMLElement>}
      >
        {children}
      </Listbox.Options>
    </Transition>
  );
});

SelectMenuOptions.displayName = 'SelectMenuOptions';

const SelectMenuOption: React.FC<IListboxOptionProps> = forwardRef<
  HTMLElement,
  IListboxOptionProps
>(({ children, className, ...rest }, forwardedRef) => {
  return (
    <Listbox.Option
      className={twMerge(
        'relative cursor-default select-none hover:bg-secondary-100',
        className
      )}
      ref={forwardedRef as Ref<HTMLElement>}
      {...rest}
    >
      {children}
    </Listbox.Option>
  );
});

SelectMenuOption.displayName = 'SelectMenuOption';

const SelectMenu: React.FC<IListBoxProps> = ({ children, ...rest }) => (
  <Listbox {...rest}>{children}</Listbox>
);

const SelectWrapper = ({
  children,
  className,
  ...rest
}: IListboxWrapperProps) => {
  return (
    <div className={twMerge('relative w-full', className)} {...rest}>
      {children}
    </div>
  );
};

export {
  SelectMenu,
  SelectMenuButton,
  SelectMenuLabel,
  SelectMenuOption,
  SelectMenuOptions,
  SelectWrapper,
};
